/**
 * 系统化的LLM调用框架
 * 专门为英语学习场景设计的AI服务抽象层
 */

import backendAI from './ai';
import vocabularyApiService, { VocabularyApiResponse } from './vocabularyApiService';

// LLM任务类型
export type LLMTaskType = 
  | 'translation'           // 翻译任务
  | 'vocabulary_explain'    // 词汇解释
  | 'phrase_explain'        // 短语解释
  | 'grammar_check'         // 语法检查
  | 'conversation_reply'    // 对话回复
  | 'practice_generation'   // 练习生成
  | 'pronunciation_guide'   // 发音指导
  | 'writing_improvement'   // 写作改进
  | 'reading_comprehension'; // 阅读理解

// 通用LLM请求接口
export interface LLMRequest {
  taskType: LLMTaskType;
  input: string;
  context?: string;
  options?: {
    temperature?: number;
    maxTokens?: number;
    language?: 'zh' | 'en';
    level?: 'beginner' | 'intermediate' | 'advanced';
  };
}

// LLM响应接口
export interface LLMResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  taskType: LLMTaskType;
  processingTime?: number;
}

// 增强的翻译评估结果
export interface EnhancedTranslationEvaluation {
  score: number;
  isCorrect: boolean;
  feedback: string;
  referenceTranslations: string[];
  vocabularyAnalysis: VocabularyExplanation[];
  phraseAnalysis: PhraseExplanation[];
  grammarIssues: string[];
  improvements: string[];
}

/**
 * 英语学习专用LLM框架
 */
class EnglishLearningLLMFramework {

  /**
   * 统一的LLM调用入口
   */
  async callLLM<T>(request: LLMRequest): Promise<LLMResponse<T>> {
    const startTime = Date.now();
    
    try {
      let result: T;
      
      switch (request.taskType) {
        case 'translation':
          result = await this.handleTranslation(request) as T;
          break;
          
        case 'vocabulary_explain':
          result = await this.handleVocabularyExplanation(request) as T;
          break;
          
        case 'phrase_explain':
          result = await this.handlePhraseExplanation(request) as T;
          break;
          
        case 'grammar_check':
          result = await this.handleGrammarCheck(request) as T;
          break;
          
        case 'conversation_reply':
          result = await this.handleConversationReply(request) as T;
          break;
          
        case 'practice_generation':
          result = await this.handlePracticeGeneration(request) as T;
          break;
          
        case 'writing_improvement':
          result = await this.handleWritingImprovement(request) as T;
          break;
          
        default:
          throw new Error(`Unsupported task type: ${request.taskType}`);
      }

      return {
        success: true,
        data: result,
        taskType: request.taskType,
        processingTime: Date.now() - startTime
      };
      
    } catch (error) {
      console.error(`LLM框架错误 (${request.taskType}):`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        taskType: request.taskType,
        processingTime: Date.now() - startTime
      };
    }
  }

  /**
   * 增强的翻译评估（包含词汇分析）
   */
  async evaluateTranslationWithVocabulary(
    chineseText: string, 
    userTranslation: string,
    referenceTranslation?: string
  ): Promise<EnhancedTranslationEvaluation> {
    
    // 先执行基础翻译评估
    const translationEval = await this.evaluateBasicTranslation(chineseText, userTranslation, referenceTranslation);
    
    // 使用后端API进行词汇分析
    const vocabularyResults = await vocabularyApiService.analyzeTextVocabulary(userTranslation);
    const vocabAnalysis = {
      words: vocabularyResults.map(vocab => ({
        word: vocab.word,
        phonetic: vocab.phonetic || '',
        definition: vocab.definition,
        difficulty: vocab.difficulty_level
      })),
      phrases: [] // 暂时为空，后续可扩展
    };
    
    // 暂时跳过语法检查以减少API调用
    const grammarCheck = { issues: [], suggestions: [] };

    return {
      ...translationEval,
      vocabularyAnalysis: vocabAnalysis.words,
      phraseAnalysis: vocabAnalysis.phrases,
      grammarIssues: grammarCheck.issues || [],
      improvements: grammarCheck.suggestions || []
    };
  }

  /**
   * 处理翻译任务
   */
  private async handleTranslation(request: LLMRequest): Promise<EnhancedTranslationEvaluation | string> {
    // 如果有context，说明这是翻译评估任务
    if (request.context) {
      return await this.evaluateTranslationWithVocabulary(request.context, request.input);
    }
    
    // 否则是简单翻译任务
    const prompt = this.createTranslationPrompt(request.input, request.context);
    return await backendAI.generateResponse(prompt);
  }

  /**
   * 处理词汇解释任务
   */
  private async handleVocabularyExplanation(request: LLMRequest): Promise<any> {
    const vocabularyResults = await vocabularyApiService.analyzeTextVocabulary(request.input);
    return vocabularyResults.length > 0 ? vocabularyResults[0] : null;
  }

  /**
   * 处理短语解释任务
   */
  private async handlePhraseExplanation(request: LLMRequest): Promise<any> {
    const vocabularyResults = await vocabularyApiService.analyzeTextVocabulary(request.input);
    return vocabularyResults.length > 0 ? vocabularyResults[0] : null;
  }

  /**
   * 处理语法检查任务
   */
  private async handleGrammarCheck(request: LLMRequest): Promise<{issues: string[], suggestions: string[]}> {
    return await this.checkGrammar(request.input);
  }

  /**
   * 处理对话回复任务
   */
  private async handleConversationReply(request: LLMRequest): Promise<string> {
    return await backendAI.generateSmartReply();
  }

  /**
   * 处理练习生成任务
   */
  private async handlePracticeGeneration(request: LLMRequest): Promise<any> {
    const prompt = this.createPracticeGenerationPrompt(request.input, request.context);
    return await backendAI.generateResponse(prompt);
  }

  /**
   * 处理写作改进任务
   */
  private async handleWritingImprovement(request: LLMRequest): Promise<{
    improvedText: string;
    changes: string[];
    score: number;
  }> {
    const prompt = this.createWritingImprovementPrompt(request.input);
    const response = await backendAI.generateResponse(prompt);
    
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('写作改进解析失败:', error);
    }

    return {
      improvedText: request.input,
      changes: ['AI服务暂时不可用'],
      score: 70
    };
  }

  /**
   * 基础翻译评估
   */
  private async evaluateBasicTranslation(
    chineseText: string, 
    userTranslation: string,
    referenceTranslation?: string
  ): Promise<Omit<EnhancedTranslationEvaluation, 'vocabularyAnalysis' | 'phraseAnalysis' | 'grammarIssues' | 'improvements'>> {
    
    const ref = referenceTranslation || await this.generateReferenceTranslation(chineseText);
    
    const prompt = `你是专业的英语教学导师，请评估学生的翻译质量：

中文原文：${chineseText}
学生翻译：${userTranslation}
标准参考：${ref}

请从以下维度评估：
1. 语义准确性 (30%)
2. 语法正确性 (25%) 
3. 表达自然性 (25%)
4. 词汇选择 (20%)

返回JSON格式评估：
{
  "score": 评分(0-100),
  "isCorrect": 是否及格(60分以上),
  "feedback": "鼓励性评价(1-2句话)",
  "referenceTranslations": ["标准翻译", "备选表达"]
}`;

    try {
      const response = await backendAI.generateResponse(prompt);
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const evaluation = JSON.parse(jsonMatch[0]);
        return {
          score: Math.max(0, Math.min(100, evaluation.score || 0)),
          isCorrect: evaluation.isCorrect || false,
          feedback: evaluation.feedback || '翻译完成',
          referenceTranslations: evaluation.referenceTranslations || [ref]
        };
      }
    } catch (error) {
      console.error('翻译评估失败:', error);
    }

    // 降级评估
    return {
      score: userTranslation.length > 0 ? 60 : 0,
      isCorrect: userTranslation.length > chineseText.length * 0.5,
      feedback: '基础评估完成',
      referenceTranslations: [ref]
    };
  }

  /**
   * 生成参考翻译
   */
  private async generateReferenceTranslation(chineseText: string): Promise<string> {
    const prompt = `你是专业翻译导师。请将下面的中文翻译成最自然地道的英文：

要求：
- 使用英语母语者的自然表达
- 选择最合适的时态和语态  
- 语法完全正确
- 语言简洁清晰
- 只返回翻译结果，无需解释

中文："${chineseText}"
英文：`;
    
    try {
      const response = await backendAI.generateResponse(prompt);
      return response.trim();
    } catch (error) {
      console.error('参考翻译生成失败:', error);
      return `Translation of: ${chineseText}`;
    }
  }

  /**
   * 语法检查
   */
  private async checkGrammar(text: string): Promise<{issues: string[], suggestions: string[]}> {
    const prompt = `请检查这段英文的语法错误：

"${text}"

返回JSON：
{
  "issues": ["错误1", "错误2"],
  "suggestions": ["改进建议1", "改进建议2"]
}

如果没有错误，返回空数组。`;

    try {
      const response = await backendAI.generateResponse(prompt);
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]);
        return {
          issues: result.issues || [],
          suggestions: result.suggestions || []
        };
      }
    } catch (error) {
      console.error('语法检查失败:', error);
    }

    return { issues: [], suggestions: [] };
  }

  /**
   * 创建翻译提示词
   */
  private createTranslationPrompt(text: string, context?: string): string {
    const contextSection = context ? `\n上下文：${context}\n` : '';
    
    return `你是专业的英语翻译导师。请将中文翻译成自然地道的英文，要求：

1. 翻译要自然流畅，符合英语母语者的表达习惯
2. 考虑语境和语气，选择最合适的词汇和句式
3. 避免直译，注重意思传达
4. 只返回翻译结果，不要解释${contextSection}

中文：${text}
英文：`;
  }

  /**
   * 创建练习生成提示词
   */
  private createPracticeGenerationPrompt(conversationText: string, difficulty?: string): string {
    return `基于以下对话内容，生成3个翻译练习题：

对话内容：
${conversationText}

要求：
- 从对话中提取真实的中文表达
- 提供地道的英文翻译
- 包含词汇和短语解释
- 难度：${difficulty || 'intermediate'}

返回JSON格式的练习数组。`;
  }

  /**
   * 创建写作改进提示词
   */
  private createWritingImprovementPrompt(text: string): string {
    return `请改进这段英文表达：

"${text}"

返回JSON：
{
  "improvedText": "改进后的文本",
  "changes": ["修改点1", "修改点2"],
  "score": 0-100分
}`;
  }
}

// 导出单例实例
const llmFramework = new EnglishLearningLLMFramework();
export default llmFramework;

// 导出类型
export type {
  LLMTaskType,
  LLMRequest,
  LLMResponse,
  EnhancedTranslationEvaluation
};